import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const NoticeModal = ({ notice, onClose }) => {
  if (!notice) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
        {/* 헤더 - 고정 */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-4 md:px-6 py-5 border-b border-purple-100 flex-shrink-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent break-all min-w-0 flex-1">
              {notice.is_important && <span className="text-red-500 mr-2">🔴</span>}
              {notice.title}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-2 hover:bg-gray-100 rounded-lg transition-all -mr-1"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* 내용 - 스크롤 가능 */}
        <div className="px-4 md:px-6 py-6 overflow-y-auto flex-1 bg-gradient-to-b from-white to-gray-50/30">
          {/* 작성 정보 */}
          <div className="flex items-center gap-4 text-sm text-gray-600 mb-6 p-3 bg-white rounded-xl shadow-sm border border-gray-100">
            <span>{notice.author_name || '관리자'}</span>
            <span>•</span>
            <span>{format(new Date(notice.created_at), 'yyyy년 M월 d일 HH:mm', { locale: ko })}</span>
            {notice.views !== undefined && (
              <>
                <span>•</span>
                <span>조회 {notice.views}</span>
              </>
            )}
          </div>

          {/* 공지 내용 */}
          <div className="prose prose-sm max-w-none break-words">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                p: ({node, ...props}) => <p className="whitespace-pre-wrap break-words" {...props} />,
                h1: ({node, ...props}) => <h1 className="break-words" {...props} />,
                h2: ({node, ...props}) => <h2 className="break-words" {...props} />,
                h3: ({node, ...props}) => <h3 className="break-words" {...props} />,
                h4: ({node, ...props}) => <h4 className="break-words" {...props} />,
                h5: ({node, ...props}) => <h5 className="break-words" {...props} />,
                h6: ({node, ...props}) => <h6 className="break-words" {...props} />,
                li: ({node, ...props}) => <li className="break-words" {...props} />,
                td: ({node, ...props}) => <td className="break-words" {...props} />,
                th: ({node, ...props}) => <th className="break-words" {...props} />,
                img: ({node, ...props}) => (
                  <div className="flex justify-center my-4">
                    <img {...props} alt="공지사항 이미지" className="max-w-full h-auto rounded-lg shadow-md" />
                  </div>
                ),
                iframe: ({node, ...props}) => {
                  // YouTube 비디오 처리
                  if (props.src && props.src.includes('youtube.com')) {
                    return (
                      <div className="relative w-full pt-[56.25%] my-4">
                        <iframe
                          {...props}
                          title="공지사항 비디오 - YouTube"
                          className="absolute top-0 left-0 w-full h-full rounded-lg shadow-md"
                          allowFullScreen
                        />
                      </div>
                    );
                  }
                  return <iframe {...props} title="공지사항 비디오" />;
                }
              }}
            >
              {notice.content || ''}
            </ReactMarkdown>
          </div>

          {/* 이미지 첨부파일 */}
          {notice.images && notice.images.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">첨부 이미지</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {notice.images.map((image, index) => (
                  <div key={index} className="flex justify-center">
                    <img
                      src={image.url || `/uploads/notices/${image.filename}`}
                      alt={`첨부 이미지 ${index + 1}`}
                      className="max-w-full h-auto rounded-lg shadow-sm border border-gray-200"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 일반 첨부파일 */}
          {notice.attachments && notice.attachments.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">첨부파일</h4>
              <div className="space-y-2">
                {notice.attachments.map((file, index) => (
                  <a
                    key={index}
                    href={file.url || `/uploads/notices/${file.filename}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                  >
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-sm text-gray-700">{file.original_name || file.filename}</span>
                    {file.size && (
                      <span className="text-xs text-gray-500">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    )}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 푸터 - 고정 */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 md:px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl font-medium"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoticeModal;